import { type ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Fade,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import RadioButtonUncheckedRoundedIcon from '@mui/icons-material/RadioButtonUncheckedRounded';
import DragIndicatorRoundedIcon from '@mui/icons-material/DragIndicatorRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  quizApi,
  type QuizForStudentDto,
  type QuizAnswerDto,
  type SubmitQuizResultDto,
} from '../../../lib/api/assignment.api';
import { formatInstant } from '@dosfilos/shared';
import { errorText } from '../../../lib/error-text';
import {
  useTenantTimeZone,
  useTenantDateFormat,
  useTenantTimeFormat,
} from '../../../stores/tenant.store';

type PublicQuestion = QuizForStudentDto['questions'][number];

/** Has the student meaningfully answered this question? Drives the progress bar
 *  and the per-question check. */
function isAnswered(q: PublicQuestion, a: QuizAnswerDto | undefined): boolean {
  switch (q.kind) {
    case 'single_choice':
      return a?.kind === 'single_choice' && !!a.selectedOptionId;
    case 'multiple_choice':
      return a?.kind === 'multiple_choice' && a.selectedOptionIds.length > 0;
    case 'matching':
      return a?.kind === 'matching' && a.pairs.length === q.left.length;
    case 'ordering':
      return a?.kind === 'ordering' && a.orderedItemIds.length === q.items.length;
    case 'numeric':
      return a?.kind === 'numeric' && a.value !== undefined;
    case 'short_answer':
      return a?.kind === 'short_answer' && a.text.trim() !== '';
    case 'cloze': {
      const blanks = [...q.template.matchAll(/\[\[(\d+)\]\]/g)].map((m) => m[1]!);
      if (a?.kind !== 'cloze') return false;
      return blanks.length > 0 && blanks.every((b) => (a.values[b] ?? '').trim() !== '');
    }
    default:
      return a?.kind === 'open' && a.text.trim() !== '';
  }
}

/** Student-facing quiz taker. Fetches the quiz WITHOUT any answer key, captures
 *  answers, submits for server-side grading and shows the resulting score. */
export function QuizTakerPanel({
  tenantId,
  studentId,
  assignmentId,
  onClose,
  onSubmitted,
}: {
  tenantId: string;
  studentId: string;
  assignmentId: string;
  onClose: () => void;
  onSubmitted: () => void;
}): JSX.Element {
  const { t } = useTranslation('sis');
  const tenantTz = useTenantTimeZone();
  const tenantFmt = useTenantDateFormat();
  const tenantTimeFmt = useTenantTimeFormat();
  const [quiz, setQuiz] = useState<QuizForStudentDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState<Record<string, QuizAnswerDto>>({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<SubmitQuizResultDto | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    quizApi
      .getForStudent({ tenantId, studentId, assignmentId })
      .then((q) => {
        if (!cancelled) setQuiz(q);
      })
      .catch((e) => {
        if (!cancelled) setErr(errorText(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId, studentId, assignmentId]);

  const setAnswer = (questionId: string, answer: QuizAnswerDto) => {
    setAnswers((prev) => ({ ...prev, [questionId]: answer }));
  };

  const handleSubmit = async () => {
    if (!quiz) return;
    setSubmitting(true);
    setErr(null);
    try {
      const res = await quizApi.submit({
        tenantId,
        assignmentId,
        answers: Object.values(answers),
      });
      setResult(res);
      onSubmitted();
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setSubmitting(false);
    }
  };

  const promptText = (p: string | Record<string, unknown>): string =>
    typeof p === 'string' ? p : '';

  const answerable = quiz && !result && !quiz.alreadySubmitted && quiz.open;
  const total = quiz?.questions.length ?? 0;
  const answeredCount = quiz
    ? quiz.questions.filter((q) => isAnswered(q, answers[q.id])).length
    : 0;

  return (
    <Card variant="outlined">
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Typography variant="h6" sx={{ flex: 1 }}>
          {quiz?.name ?? t('portal.assignmentDetail.quiz.takeQuiz')}
        </Typography>
        <Button size="small" onClick={onClose} disabled={submitting}>
          {result
            ? t('portal.assignmentDetail.quiz.close')
            : t('portal.assignmentDetail.quiz.cancel')}
        </Button>
      </Box>

      {answerable && (
        <Box sx={{ px: 2, pt: 1.5 }}>
          <Stack direction="row" justifyContent="space-between" mb={0.5}>
            <Typography variant="caption" color="text.secondary">
              {t('portal.assignmentDetail.quiz.progress')}
            </Typography>
            <Typography variant="caption" color="text.secondary" fontWeight={700}>
              {answeredCount}/{total}
            </Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={total ? (answeredCount / total) * 100 : 0}
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Box>
      )}

      <CardContent>
        {loading ? (
          <Box textAlign="center" py={4}>
            <CircularProgress />
          </Box>
        ) : err && !quiz ? (
          <Alert severity="error">{err}</Alert>
        ) : result ? (
          <Fade in>
            <Stack spacing={1.5} py={1}>
              <Typography variant="h6">{t('portal.assignmentDetail.quiz.resultTitle')}</Typography>
              {result.pendingReview ? (
                // Mixed quiz with open questions: the total grade is NOT finalized —
                // it waits for the teacher's review. Do NOT show a partial score, so
                // the student doesn't mistake the closed subtotal for their grade.
                <Alert severity="info" sx={{ py: 0.5 }}>
                  {t('portal.assignmentDetail.quiz.submittedPendingReview')}
                </Alert>
              ) : (
                <>
                  {result.passed !== undefined && (
                    <Alert severity={result.passed ? 'success' : 'error'} sx={{ py: 0.5 }}>
                      {result.passed
                        ? t('portal.assignmentDetail.quiz.passed')
                        : t('portal.assignmentDetail.quiz.failed')}
                    </Alert>
                  )}
                  <Typography variant="body1">
                    {t('portal.assignmentDetail.quiz.scoreLine', {
                      raw: round(result.rawPoints),
                      max: round(result.maxPoints),
                    })}
                  </Typography>
                  {result.latePenalty > 0 && (
                    <Alert severity="warning" sx={{ py: 0.5 }}>
                      {t('portal.assignmentDetail.quiz.latePenaltyApplied', {
                        pct: Math.round(result.latePenalty * 100),
                        final: round(result.finalPoints),
                      })}
                    </Alert>
                  )}
                </>
              )}
            </Stack>
          </Fade>
        ) : quiz && quiz.alreadySubmitted ? (
          <Alert severity="info">{t('portal.assignmentDetail.quiz.alreadySubmitted')}</Alert>
        ) : quiz && !quiz.open ? (
          <Alert severity="warning">
            {quiz.closesAt && new Date(quiz.closesAt) <= new Date()
              ? t('portal.assignmentDetail.quiz.closedNotice')
              : quiz.opensAt
                ? t('portal.assignmentDetail.quiz.opensAtNotice', {
                    date: formatInstant(quiz.opensAt, tenantTz, tenantFmt, tenantTimeFmt),
                  })
                : t('portal.assignmentDetail.quiz.notOpenNotice')}
          </Alert>
        ) : quiz ? (
          <Fade in>
            <Stack spacing={3} py={1}>
              {quiz.timeLimitMinutes && (
                <Typography variant="caption" color="text.secondary">
                  {t('portal.assignmentDetail.quiz.timeLimit', { min: quiz.timeLimitMinutes })}
                </Typography>
              )}
              {err && <Alert severity="error">{err}</Alert>}
              {quiz.questions.map((q, idx) => {
                const answered = isAnswered(q, answers[q.id]);
                return (
                  <Box key={q.id}>
                    <Stack direction="row" alignItems="center" spacing={0.75} mb={1}>
                      {answered ? (
                        <CheckCircleRoundedIcon color="success" fontSize="small" />
                      ) : (
                        <RadioButtonUncheckedRoundedIcon
                          fontSize="small"
                          sx={{ color: 'text.disabled' }}
                        />
                      )}
                      <Typography variant="subtitle1" fontWeight={700}>
                        {idx + 1}. {promptText(q.prompt)}{' '}
                        <Typography component="span" variant="caption" color="text.secondary">
                          ({t('portal.assignmentDetail.quiz.pts', { count: q.points })})
                        </Typography>
                      </Typography>
                    </Stack>
                    <QuestionInput
                      q={q}
                      answer={answers[q.id]}
                      onAnswer={(a) => setAnswer(q.id, a)}
                    />
                    {idx < quiz.questions.length - 1 && <Divider sx={{ mt: 3 }} />}
                  </Box>
                );
              })}
            </Stack>
          </Fade>
        ) : null}
      </CardContent>

      {answerable && (
        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderTop: 1,
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 1.5,
          }}
        >
          {answeredCount < total && (
            <Typography variant="caption" color="text.secondary">
              {t('portal.assignmentDetail.quiz.remaining', { count: total - answeredCount })}
            </Typography>
          )}
          <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
            {submitting
              ? t('portal.assignmentDetail.quiz.submitting')
              : t('portal.assignmentDetail.quiz.submit')}
          </Button>
        </Box>
      )}
    </Card>
  );
}

function round(n: number): number {
  return Math.round(n * 100) / 100;
}

/** A big, tappable option with a letter badge (A, B, C…). The badge fills with a
 *  check when selected. Multi-select uses a rounded-square badge, single a circle. */
function OptionCard({
  selected,
  multi,
  index,
  label,
  onClick,
}: {
  selected: boolean;
  multi: boolean;
  index: number;
  label: string;
  onClick: () => void;
}): JSX.Element {
  const letter = String.fromCharCode(65 + index);
  return (
    <Box
      onClick={onClick}
      role="button"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 1.5,
        py: 1.25,
        borderRadius: 2,
        border: 2,
        borderColor: selected ? 'primary.main' : 'transparent',
        bgcolor: (th) =>
          selected ? alpha(th.palette.primary.main, 0.16) : alpha(th.palette.text.primary, 0.05),
        boxShadow: selected ? 2 : 0,
        cursor: 'pointer',
        userSelect: 'none',
        transition: 'all .15s ease',
        '&:hover': {
          bgcolor: (th) =>
            selected ? alpha(th.palette.primary.main, 0.22) : alpha(th.palette.text.primary, 0.09),
          transform: 'translateY(-2px)',
          boxShadow: 3,
        },
      }}
    >
      <Box
        sx={{
          width: 32,
          height: 32,
          flexShrink: 0,
          borderRadius: multi ? 1.5 : '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 800,
          fontSize: 15,
          border: 2,
          borderColor: selected ? 'primary.main' : (th) => alpha(th.palette.text.primary, 0.25),
          bgcolor: (th) => (selected ? th.palette.primary.main : 'transparent'),
          color: (th) => (selected ? th.palette.primary.contrastText : th.palette.text.secondary),
          transition: 'all .15s ease',
        }}
      >
        {selected ? <CheckRoundedIcon fontSize="small" /> : letter}
      </Box>
      <Typography variant="body1">{label}</Typography>
    </Box>
  );
}

function SortableOrderItem({
  id,
  index,
  text,
}: {
  id: string;
  index: number;
  text: string | undefined;
}): JSX.Element {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });
  return (
    <Box
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      sx={{
        transform: CSS.Transform.toString(transform),
        transition,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 1.5,
        py: 1.25,
        borderRadius: 2,
        border: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
        boxShadow: isDragging ? 4 : 0,
        opacity: isDragging ? 0.9 : 1,
        cursor: 'grab',
        touchAction: 'none',
        '&:hover': { borderColor: 'primary.light' },
      }}
    >
      <DragIndicatorRoundedIcon fontSize="small" sx={{ color: 'text.disabled' }} />
      <Typography variant="body2" sx={{ width: 20, color: 'text.secondary' }}>
        {index + 1}.
      </Typography>
      <Typography variant="body1" sx={{ flex: 1 }}>
        {text}
      </Typography>
    </Box>
  );
}

function OrderingInput({
  q,
  answer,
  onAnswer,
}: {
  q: Extract<PublicQuestion, { kind: 'ordering' }>;
  answer: QuizAnswerDto | undefined;
  onAnswer: (a: QuizAnswerDto) => void;
}): JSX.Element {
  const { t } = useTranslation('sis');
  const current =
    answer?.kind === 'ordering' && answer.orderedItemIds.length === q.items.length
      ? answer.orderedItemIds
      : q.items.map((i) => i.id);
  const textById = new Map(q.items.map((i) => [i.id, i.text]));
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const onDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIdx = current.indexOf(String(active.id));
    const newIdx = current.indexOf(String(over.id));
    if (oldIdx < 0 || newIdx < 0) return;
    onAnswer({
      kind: 'ordering',
      questionId: q.id,
      orderedItemIds: arrayMove(current, oldIdx, newIdx),
    });
  };

  return (
    <Stack spacing={0.5}>
      <Typography variant="caption" color="text.secondary" mb={0.5}>
        {t('portal.assignmentDetail.quiz.dragHint')}
      </Typography>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={current} strategy={verticalListSortingStrategy}>
          <Stack spacing={1}>
            {current.map((id, i) => (
              <SortableOrderItem key={id} id={id} index={i} text={textById.get(id)} />
            ))}
          </Stack>
        </SortableContext>
      </DndContext>
    </Stack>
  );
}

/** A draggable chip representing a right-column item in a matching question. */
function MatchChip({ id, label }: { id: string; label: string }): JSX.Element {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id });
  return (
    <Box
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        px: 1.25,
        py: 0.75,
        borderRadius: 2,
        border: 1,
        borderColor: 'primary.light',
        bgcolor: (th) => alpha(th.palette.primary.main, 0.14),
        color: 'text.primary',
        fontWeight: 600,
        cursor: 'grab',
        touchAction: 'none',
        userSelect: 'none',
        transform: CSS.Translate.toString(transform),
        boxShadow: isDragging ? 4 : 0,
        opacity: isDragging ? 0.85 : 1,
        zIndex: isDragging ? 10 : 'auto',
        transition: 'box-shadow .15s ease',
      }}
    >
      <DragIndicatorRoundedIcon fontSize="small" sx={{ color: 'text.disabled' }} />
      {label}
    </Box>
  );
}

/** A drop target: a left-item slot, or the pool of unassigned chips. */
function MatchDropZone({
  id,
  empty,
  emptyLabel,
  children,
}: {
  id: string;
  empty: boolean;
  emptyLabel: string;
  children?: ReactNode;
}): JSX.Element {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <Box
      ref={setNodeRef}
      sx={{
        minHeight: 44,
        minWidth: 180,
        px: 1,
        py: 0.75,
        borderRadius: 2,
        border: 2,
        borderStyle: 'dashed',
        borderColor: isOver ? 'primary.main' : (th) => alpha(th.palette.text.primary, 0.2),
        bgcolor: (th) => (isOver ? alpha(th.palette.primary.main, 0.1) : 'transparent'),
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        flexWrap: 'wrap',
        transition: 'all .15s ease',
      }}
    >
      {empty ? (
        <Typography variant="caption" color="text.disabled">
          {emptyLabel}
        </Typography>
      ) : (
        children
      )}
    </Box>
  );
}

function MatchingInput({
  q,
  answer,
  onAnswer,
}: {
  q: Extract<PublicQuestion, { kind: 'matching' }>;
  answer: QuizAnswerDto | undefined;
  onAnswer: (a: QuizAnswerDto) => void;
}): JSX.Element {
  const { t } = useTranslation('sis');
  const pairs = answer?.kind === 'matching' ? answer.pairs : [];
  const rightById = new Map(q.right.map((r) => [r.id, r.text]));
  const rightForLeft = new Map(pairs.map((p) => [p.leftId, p.rightId]));
  const assignedRightIds = new Set(pairs.map((p) => p.rightId));
  const pool = q.right.filter((r) => !assignedRightIds.has(r.id));
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const onDragEnd = (e: DragEndEvent) => {
    const rightId = String(e.active.id);
    const over = e.over ? String(e.over.id) : null;
    if (!over) return;
    // Detach this chip from wherever it currently sits.
    let next = pairs.filter((p) => p.rightId !== rightId);
    if (over.startsWith('slot:')) {
      const leftId = over.slice(5);
      next = next.filter((p) => p.leftId !== leftId); // free the slot (its old chip → pool)
      next = [...next, { leftId, rightId }];
    }
    // over === 'pool' → leave detached (unassigned)
    onAnswer({ kind: 'matching', questionId: q.id, pairs: next });
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <Stack spacing={1}>
        <Typography variant="caption" color="text.secondary">
          {t('portal.assignmentDetail.quiz.matchingDragHint')}
        </Typography>
        {q.left.map((l) => {
          const rid = rightForLeft.get(l.id);
          return (
            <Stack key={l.id} direction="row" alignItems="center" spacing={1.5}>
              <Typography variant="body1" sx={{ flex: 1 }}>
                {l.text}
              </Typography>
              <MatchDropZone
                id={`slot:${l.id}`}
                empty={!rid}
                emptyLabel={t('portal.assignmentDetail.quiz.dropHere')}
              >
                {rid && <MatchChip id={rid} label={rightById.get(rid) ?? ''} />}
              </MatchDropZone>
            </Stack>
          );
        })}
        <Divider sx={{ my: 0.5 }} />
        <Typography variant="caption" color="text.secondary">
          {t('portal.assignmentDetail.quiz.matchOptions')}
        </Typography>
        <MatchDropZone
          id="pool"
          empty={pool.length === 0}
          emptyLabel={t('portal.assignmentDetail.quiz.allPlaced')}
        >
          {pool.map((r) => (
            <MatchChip key={r.id} id={r.id} label={r.text} />
          ))}
        </MatchDropZone>
      </Stack>
    </DndContext>
  );
}

function QuestionInput({
  q,
  answer,
  onAnswer,
}: {
  q: PublicQuestion;
  answer: QuizAnswerDto | undefined;
  onAnswer: (a: QuizAnswerDto) => void;
}): JSX.Element {
  const { t } = useTranslation('sis');

  if (q.kind === 'single_choice') {
    const selected = answer?.kind === 'single_choice' ? (answer.selectedOptionId ?? '') : '';
    return (
      <Stack spacing={1}>
        {q.options.map((opt, i) => (
          <OptionCard
            key={opt.id}
            multi={false}
            index={i}
            selected={selected === opt.id}
            label={opt.text}
            onClick={() =>
              onAnswer({ kind: 'single_choice', questionId: q.id, selectedOptionId: opt.id })
            }
          />
        ))}
      </Stack>
    );
  }

  if (q.kind === 'multiple_choice') {
    const selectedIds = answer?.kind === 'multiple_choice' ? answer.selectedOptionIds : [];
    const toggle = (optId: string) => {
      const next = new Set(selectedIds);
      if (next.has(optId)) next.delete(optId);
      else next.add(optId);
      onAnswer({ kind: 'multiple_choice', questionId: q.id, selectedOptionIds: [...next] });
    };
    return (
      <Stack spacing={1}>
        {q.options.map((opt, i) => (
          <OptionCard
            key={opt.id}
            multi
            index={i}
            selected={selectedIds.includes(opt.id)}
            label={opt.text}
            onClick={() => toggle(opt.id)}
          />
        ))}
      </Stack>
    );
  }

  if (q.kind === 'matching') {
    return <MatchingInput q={q} answer={answer} onAnswer={onAnswer} />;
  }

  if (q.kind === 'ordering') {
    return <OrderingInput q={q} answer={answer} onAnswer={onAnswer} />;
  }

  if (q.kind === 'numeric') {
    const value = answer?.kind === 'numeric' ? answer.value : undefined;
    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <TextField
          type="number"
          placeholder={t('portal.assignmentDetail.quiz.yourAnswer')}
          value={value ?? ''}
          onChange={(e) =>
            onAnswer(
              e.target.value === ''
                ? { kind: 'numeric', questionId: q.id }
                : { kind: 'numeric', questionId: q.id, value: Number(e.target.value) },
            )
          }
        />
        {q.unit && <Typography variant="body1">{q.unit}</Typography>}
      </Stack>
    );
  }

  if (q.kind === 'cloze') {
    const values = answer?.kind === 'cloze' ? answer.values : {};
    const parts = q.template.split(/(\[\[\d+\]\])/g);
    const setBlank = (blankId: string, text: string) =>
      onAnswer({ kind: 'cloze', questionId: q.id, values: { ...values, [blankId]: text } });
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75 }}>
        {parts.map((part, i) => {
          const m = part.match(/^\[\[(\d+)\]\]$/);
          if (m) {
            const blankId = m[1]!;
            return (
              <TextField
                key={`b-${i}`}
                size="small"
                variant="outlined"
                sx={{ width: 130 }}
                value={values[blankId] ?? ''}
                onChange={(e) => setBlank(blankId, e.target.value)}
              />
            );
          }
          return (
            <Typography key={`t-${i}`} variant="body1" component="span">
              {part}
            </Typography>
          );
        })}
      </Box>
    );
  }

  if (q.kind === 'short_answer') {
    const text = answer?.kind === 'short_answer' ? answer.text : '';
    return (
      <TextField
        fullWidth
        placeholder={t('portal.assignmentDetail.quiz.yourAnswer')}
        value={text}
        onChange={(e) => onAnswer({ kind: 'short_answer', questionId: q.id, text: e.target.value })}
      />
    );
  }

  // open — declared for completeness; graded by the teacher in Delivery 2.
  const text = answer?.kind === 'open' ? answer.text : '';
  return (
    <TextField
      fullWidth
      multiline
      minRows={3}
      placeholder={t('portal.assignmentDetail.quiz.yourAnswer')}
      value={text}
      onChange={(e) => onAnswer({ kind: 'open', questionId: q.id, text: e.target.value })}
    />
  );
}
